import {
  formatCampaignDollars,
  GIFT_PASS_FALLBACK_PRICE_CENTS,
  type GiftPassBundlePreviewPrice,
} from '@bonsai/shared';

import { GiftPassTag } from '../tags/GiftPassTag';

import type { BundleFormValues } from './bundle-form';

import './gift-pass-bundle-editor.css';

export interface BundleCustomerPreviewProps {
  values: BundleFormValues;
  /** The bundle at the fallback price, once the terms are valid and priced. */
  atFallback: GiftPassBundlePreviewPrice | null;
}

const dollars = (cents: number | null | undefined) =>
  cents === null || cents === undefined ? '—' : formatCampaignDollars(cents);

/** The saving as a whole percentage of the undiscounted price, as the purchase page rounds it. */
function savingPercent(price: GiftPassBundlePreviewPrice | null): number {
  if (!price?.savingCents || price.listPriceCents <= 0) return 0;
  return Math.round((price.savingCents * 100) / price.listPriceCents);
}

export function BundleCustomerPreview({ values, atFallback }: BundleCustomerPreviewProps) {
  const percentOff = savingPercent(atFallback);
  const tags = values.tags.filter((tag) => tag.text.trim());
  return (
    <section className="nb-card gpb-ed-side-card" aria-labelledby="bundle-preview-title">
      <h2 id="bundle-preview-title" className="nb-eyebrow gpb-ed-side-title">
        On the purchase page
      </h2>
      <div
        className="gpb-ed-card-preview"
        data-promoted={values.isPromoted}
        data-inactive={!values.isActive}
      >
        {tags.length > 0 && (
          <ul className="gp-tag-list gpb-ed-card-tags" aria-label="Tags">
            {tags.map((tag) => (
              <li key={tag.key}>
                <GiftPassTag tag={{ text: tag.text.trim(), color: tag.color }} />
              </li>
            ))}
          </ul>
        )}
        <div className="gpb-ed-card-head">
          <span className="gpb-ed-card-name">{values.name.trim() || 'Bundle name'}</span>
          <span className="gpb-ed-card-each">
            {percentOff > 0 && atFallback && (
              <s className="gpb-ed-card-was">{dollars(atFallback.basePriceCents)}</s>
            )}{' '}
            <strong>{dollars(atFallback?.salePricePerPassCents)}</strong> each
          </span>
        </div>
        {percentOff > 0 && <div className="gpb-ed-card-save">Save {percentOff}%</div>}
        <span className="gpb-ed-grab" aria-hidden>
          Grab
        </span>
      </div>
      <p className="gpb-ed-side-note">
        {values.isActive
          ? `Example at ${formatCampaignDollars(GIFT_PASS_FALLBACK_PRICE_CENTS)} a pass. Pass prices follow local ticket prices.`
          : 'Hidden from customers while the bundle is disabled.'}
      </p>
    </section>
  );
}
